// Display helpers of the Variant History tab (FIG-239 · UC-256). Pure, so they are tested on their own.
// The figures are stored values: nothing here recomputes one, it only formats it.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "26 Oct 2026" for '2026-10-26' (the prototype's date format). A PT calendar date, never shifted by the browser's zone. */
export function formatSnapshotDate(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

/** REQ-060: the $ sits in the cell, negatives read -$1,234.56. */
export function formatMoney(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const formatted = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return value < 0 && formatted !== '0.00' ? `-$${formatted}` : `$${formatted}`;
}

/** Whole units: "1,200". */
export function formatWholeNumber(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return Math.round(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** The stored DR% with two decimals: "20.00%". */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${value.toFixed(2)}%`;
}

/** Days until stockout in whole days; no value means no units sold in the window. */
export function formatDaysUntilStockout(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'No sales';
  return formatWholeNumber(value);
}

/** GMV (All Time) is null when the merchant has no sales history. */
export function formatAllTimeGmv(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return 'No data';
  return formatMoney(value);
}

/** "Not recorded: <reason>", without repeating the words when the stored reason already starts with them. */
export function notRecordedText(reason: string | null | undefined): string {
  const text = (reason ?? '').trim();
  if (!text) return 'Not recorded';
  return /^not recorded\b/i.test(text) ? text : `Not recorded: ${text}`;
}

export type PageItem = number | 'ellipsis-start' | 'ellipsis-end';

/**
 * Page numbers with long ranges collapsed: 1 2 3 4 … 21 at the start, 1 … 9 10 11 … 21 in the middle,
 * 1 … 18 19 20 21 at the end. Up to 7 pages show every number.
 */
export function pageItems(current: number, total: number): PageItem[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (current <= 3) return [1, 2, 3, 4, 'ellipsis-end', total];
  if (current >= total - 2) return [1, 'ellipsis-start', total - 3, total - 2, total - 1, total];
  return [1, 'ellipsis-start', current - 1, current, current + 1, 'ellipsis-end', total];
}
